import React, {Component} from 'react';
import {App} from '../../app/AppSingleton';
import {AppSignals} from '../../app/AppSignals';
import {ViewMode, ViewModeEnum} from '../../models/view-types';
import type {Board, Board as BoardType, Column} from '../../models/types';
import {Icons} from '../../constants/icons';
import ColumnComponent from '../columns/Column';
import ColumnAdd from '../edit/ColumnAdd';
import ColumnDropzone from '../edit/ColumnDropzone';
import BoardFilters from './BoardFilters';
import {BoardNav} from './BoardNav';
import './Board.css';
import './Board.columns.css';
import './Board.list.css';
import {Disposer} from "../../app/Signals";
import {DndSystem} from '../../dnd-system/core/DndSystem';
import type {DragData, DropPosition, DropZoneContext, DropZoneStrategy} from '../../dnd-system/types';
import {DEFAULT_SCROLL_CONFIG} from "../../dnd-system";

interface BoardProps {
	children?: React.ReactNode;
	board:BoardType;
}

interface BoardState {
	viewMode: ViewMode;
	showFilters: boolean;
	isAddingTask: boolean;
	addingTaskColumnId: string | null;
	isGrabbing: boolean;
	dragStartX: number;
	dragStartScrollLeft: number;
	currentColumnIndex: number;
	scrollIndicators: { left: boolean; right: boolean };
	isSwiping: boolean;
	swipeStartX: number;
	touchStartTime: number; // Время начала касания
	swipeVelocity: number; // Скорость свайпа
	touchDeltaX: number; // Общее смещение пальца по X за время касания
}

class BoardComponent extends Component<BoardProps, BoardState> {
	private app: App;
	private dndSystem = DndSystem.getInstance({
		scroll: {
			...DEFAULT_SCROLL_CONFIG,
			scrollableSelectors: [".column__tasks", ".board"]
		}});
	private boardContainerRef: React.RefObject<HTMLDivElement>;
	private disposer = new Disposer();
	private columnDraggables: Map<string, () => void> = new Map();
	private boardDropZoneUnregister?: () => void;
	private isProgrammaticScroll = false;

	//<editor-fold desc="Init">
	constructor(props: BoardProps) {
		super(props);
		this.app = App.getInstance();
		this.boardContainerRef = React.createRef();

		const viewMode = this.app.getCurrentViewMode();

		this.state = {
			viewMode,
			showFilters: this.app.getState().showFilters || false,
			isAddingTask: false,
			addingTaskColumnId: null,
			isGrabbing: false,
			dragStartX: 0,
			dragStartScrollLeft: 0,
			currentColumnIndex: 0,
			scrollIndicators: { left: false, right: false },
			isSwiping: false,
			swipeStartX: 0,
			touchStartTime: 0,
			swipeVelocity: 0,
			touchDeltaX: 0,
		};
	}

	// ==================== Lifecycle Methods ====================

	componentDidMount(): void {
		// Подписка на обновление доски
		this.disposer.register(AppSignals.boardUpdated, (targetBoard:Board) => {
			const newBoard = this.getBoard();
			this.forceUpdate();

			// Простая логика: если редактирование включено - регистрируем, если выключено - очищаем
			if (newBoard.isEditing) {
				// Регистрируем draggable для колонок в ЛЮБОМ режиме просмотра
				this.setupColumnDraggables(newBoard);
			} else {
				// Очищаем draggable при выключении редактирования
				this.cleanupColumnDraggables();
			}
		});

		this.disposer.register(AppSignals.columnEditFinish, (columnId)=>{
			this.setupColumnDraggables(this.getBoard());
		})
		this.disposer.register(AppSignals.columnEditStart, (columnId)=>{
			this.cleanupColumnDraggables();
		})

		// Подписка на изменения режима просмотра
		this.disposer.register(AppSignals.viewModeChanged, (mode: ViewMode) => {
			this.setState({
				viewMode: mode,
				// Сбрасываем состояние при смене режима
				isAddingTask: false,
				addingTaskColumnId: null,
				isGrabbing: false,
				isSwiping: false,
			});

			// Разное поведение для board drop zone и scroll
			if (mode === ViewModeEnum.Columns) {
				this.registerBoardDropZone();
				this.updateScrollIndicators();
				window.addEventListener('resize', this.handleResize);
			} else {
				this.unregisterBoardDropZone();
				window.removeEventListener('resize', this.handleResize);
			}
			
			// Для column draggables: если редактирование активно - перерегистрируем
			const currentBoard = this.getBoard();
			if (currentBoard.isEditing) {
				this.setupColumnDraggables(currentBoard);
			}
		});

		// Подписка на сигнал подсветки и скролла к задаче
		this.disposer.register(AppSignals.highlightAndScrollToTask, (data) => {
			// В режиме колонок может потребоваться прокрутка к колонке
			if (this.state.viewMode === ViewModeEnum.List && this.boardContainerRef.current) {
				// Найдем элемент колонки, содержащей задачу
				const targetColumnElement = document.getElementById(data.columnId);
				if (targetColumnElement) {
					// Прокрутим контейнер доски к колонке, но с небольшой задержкой,
					// чтобы дать возможность колонке обработать сигнал первее
					setTimeout(() => {
						targetColumnElement.scrollIntoView({
							behavior: 'smooth',
							block: 'nearest',
							inline: 'center' // Центрируем колонку по горизонтали
						});
					}, 200); // Задержка, чтобы дать колонке обработать сигнал первее
				}
			}
		});

		// Подписка на изменения фильтров
		this.disposer.register(AppSignals.filtersToggled, (showFilters: boolean) => {
			this.setState({ showFilters });
		});

		this.disposer.register(AppSignals.cancelTaskAdd, (column: string) => {
			this.setState({ isAddingTask: false });
		});

		// Инициализация для режима колонок
		if (this.state.viewMode === ViewModeEnum.Columns) {
			this.registerBoardDropZone();
			this.updateScrollIndicators();
			window.addEventListener('resize', this.handleResize);
		}
	}

	componentWillUnmount(): void {
		this.disposer.dispose();
		this.unregisterBoardDropZone();
		this.cleanupColumnDraggables();
		window.removeEventListener('resize', this.handleResize);
	}
	//</editor-fold>

	//<editor-fold desc="DndSystem Integration">

	private registerBoardDropZone(): void {
		if (!this.boardContainerRef.current) return;

		const context: DropZoneContext = {
			element: this.boardContainerRef.current,
			id: 'board-container',
			acceptTypes: ['task']
		};

		const strategy: DropZoneStrategy = {
			calculateDropPosition: (
				x: number,
				y: number,
				context: DropZoneContext,
				dragData: DragData
			): DropPosition | null => {
				const element = context.element;
				const rect = element.getBoundingClientRect();

				const edgeThreshold = 50;
				const nearLeft = x < rect.left + edgeThreshold;
				const nearRight = x > rect.right - edgeThreshold;

				if (nearLeft || nearRight) {
					return {
						type: 'auto-scroll',
						relativeX: (x - rect.left) / rect.width,
						relativeY: (y - rect.top) / rect.height,
						data: {
							direction: nearLeft ? 'left' : 'right',
							intensity: Math.max(0, 1 - Math.min(
								nearLeft ? (x - rect.left) / edgeThreshold : (rect.right - x) / edgeThreshold,
								1
							))
						}
					};
				}

				return null;
			},

			onDragEnter: (context: DropZoneContext, dragData: DragData, position: DropPosition) => {
				this.updateScrollIndicators();
			},

			onDragLeave: (context: DropZoneContext, dragData: DragData) => {
				this.setState({
					scrollIndicators: { left: false, right: false }
				});
			}
		};

		this.boardDropZoneUnregister = this.dndSystem.registerDropZone({
			context,
			strategy
		});
	}

	private unregisterBoardDropZone(): void {
		if (this.boardDropZoneUnregister) {
			this.boardDropZoneUnregister();
			this.boardDropZoneUnregister = undefined;
		}
	}

	private setupColumnDraggables(board: BoardType): void {
		// ВСЕГДА очищаем старые draggable
		this.cleanupColumnDraggables();
		
		// Регистрируем новые ТОЛЬКО если редактирование активно
		if (!board.isEditing) {
			console.log('[DnD] Board is not editing, skipping draggable registration');
			return;
		}
		
		// В режиме списка работаем со свернутыми колонками
		// В режиме колонок - с обычными
		const isListMode = this.state.viewMode === ViewModeEnum.List;
		
		if (!isListMode && this.state.viewMode !== ViewModeEnum.Columns) return;

		setTimeout(() => {
			// В режиме списка используем заголовки свернутых колонок
			// В режиме колонок - стандартные drag-handle
			// В свернутом режиме колонка имеет структуру:
			// .column--view_list.column--collapsed > .column__collapsed-view > button
			// В режиме списка используем всю свернутую колонку для DnD
			// В режиме колонок - всю колонку для DnD
			// Требования: drag-handle убрать, вся колонка становится draggable
			const selector = isListMode
				? '.column--view_list.column--collapsed'
				: '.column--view_columns';

			// console.log(`[DnD] Searching for draggable elements with selector: ${selector}`);
			const columnElements = document.querySelectorAll(selector);
			// console.log(`[DnD] Found ${columnElements.length} elements`);

			columnElements.forEach((element, index) => {
				if (!(element instanceof HTMLElement)) return;

				const columnId = this.getColumnIdFromElement(element);
				// console.log(`[DnD] Element ${index}:`, element, 'Column ID:', columnId);

				if (!columnId) return;

				const column = board.columns.find(c => c.id === columnId);

				// ЗАМЕЩАЕМ ОБРАБОТЧИКИ - вся колонка становится draggable
				// Убираем обычные обработчики кликов, если они есть
				if (isListMode && board.isEditing) {
					// Для режима списка: отключаем разворачивание
					element.style.cursor = 'move';
					// Заменяем onclick чтобы не мешал DnD
					element.onclick = null;
					element.onmousedown = null;
					element.ontouchstart = null;
				} else if (!isListMode) {
					// Для режима колонок: настраиваем всю колонку как draggable
					element.style.cursor = 'move';
				}

				if (!column) {
					console.warn(`[DnD] Column ${columnId} not found in board`);
					return;
				}

				// console.log(`[DnD] Registering draggable for column: ${column.title}`);
				this.registerColumnDraggable(element, column);
			});
		}, 0);	}

	private getColumnIdFromElement(element: HTMLElement): string | null {
		// Ищем ближайшего родителя с классом column или data-column-id
		let current: HTMLElement | null = element;

		while (current && current !== document.body) {
			// Проверяем атрибут data-column-id на самом элементе (для кнопок в свернутом режиме)
			const columnIdAttr = current.getAttribute('data-column-id');
			if (columnIdAttr) {
				return columnIdAttr;
			}

			if (current.classList.contains('column')) {
				return current.id;
			}

			current = current.parentElement;
		}

		return null;
	}

	private registerColumnDraggable(element: HTMLElement, column: Column): void {
		const unregister = this.dndSystem.registerDraggable({
			context:{
				element,
				id: column.id,
				type: 'column' as const,
				data: {
					columnId: column.id,
					order: column.order,
					title: column.title,
				}
			},
			strategy:{
				cssClasses: {
					activating: 'column--drag-activating',
					dragging: 'column--drag-dragging'
				},

				onActivate: (context: any) => {
					context.element.classList.add('column--drag-activating');
				},

				onDragStart: (context: any, dragData: any) => {
					context.element.classList.remove('column--drag-activating');
					context.element.classList.add('column--drag-dragging');
					AppSignals.columnDragStarted.emit(dragData.data);
				},

				onDragEnd: (context: any, result: any) => {
					context.element.classList.remove('column--drag-activating', 'column--drag-dragging');
					AppSignals.columnDragEnded.emit(result);
				},

				onDragCancel: (context: any) => {
					context.element.classList.remove('column--drag-activating', 'column--drag-dragging');
				},

				onScrollActivation: (context: any) => {
					context.element.classList.remove('column--drag-activating');
				},
				onShortClick:(context, event)=>{
					(context.element as any).onDndClick(event)
				}

			},
			config:{
				movementThreshold: 10,
				preventScroll: false,
				disabled: false
			}
		});

		this.columnDraggables.set(column.id, unregister);
	}

	private cleanupColumnDraggables(): void {
		this.columnDraggables.forEach(unregister => unregister());
		this.columnDraggables.clear();
	}
	//</editor-fold>

	//<editor-fold desc="Helper Methods">

	private getSortedColumns(): Column[] {
		return [...this.getBoard().columns].sort((a, b) => a.order - b.order);
	}
	//</editor-fold>

	//<editor-fold desc="Scroll Management">

	private handleResize = (): void => {
		this.updateScrollIndicators();
	};

	private updateScrollIndicators = (): void => {
		if (!this.boardContainerRef.current || this.state.viewMode !== ViewModeEnum.Columns) {
			return;
		}

		const container = this.boardContainerRef.current;
		const scrollLeft = container.scrollLeft;
		const scrollWidth = container.scrollWidth;
		const clientWidth = container.clientWidth;

		this.setState({
			scrollIndicators: {
				left: scrollLeft > 10,
				right: scrollLeft < scrollWidth - clientWidth - 10,
			},
		});
	};

	private handleColumnsScroll = (): void => {
		if (this.isProgrammaticScroll) {
			this.isProgrammaticScroll = false;
			return;
		}

		this.updateScrollIndicators();

		if (!this.boardContainerRef.current || this.state.viewMode !== ViewModeEnum.Columns) {
			return;
		}

		const container = this.boardContainerRef.current;
		const scrollLeft = container.scrollLeft;
		const columnWidth = container.clientWidth * 0.85;
		const newIndex = Math.round(scrollLeft / columnWidth);
		const sortedColumns = this.getSortedColumns();

		if (newIndex >= 0 && newIndex < sortedColumns.length && newIndex !== this.state.currentColumnIndex) {
			this.setState({ currentColumnIndex: newIndex });
		}
	};

	private scrollToColumn = (index: number): void => {
		if (!this.boardContainerRef.current ||
			index < 0 ||
			index >= this.getSortedColumns().length) {
			return;
		}

		const container = this.boardContainerRef.current;
		const columnWidth = container.clientWidth * 0.85;
		
		// Всегда позиционируем по ЛЕВОЙ ГРАНИЦЕ колонки
		const targetScroll = index * columnWidth;

		this.isProgrammaticScroll = true;
		container.scrollTo({
			left: targetScroll,
			behavior: 'smooth',
		});

		this.setState({ currentColumnIndex: index });
	};

	//</editor-fold>
	//<editor-fold desc="Manual Scroll Handlers">

	private handleColumnsWheel = (e: React.WheelEvent<HTMLDivElement>): void => {
		if (this.state.viewMode !== ViewModeEnum.Columns) return;

		if (this.dndSystem.isDragActive()) return;

		const target = e.target as HTMLElement;
		const isInteractiveElement =
			target.closest('.task-card') ||
			target.closest('button') ||
			target.closest('input') ||
			target.closest('textarea') ||
			target.closest('select') ||
			target.closest('[role="button"]') ||
			target.closest('.sortable-task');

		if (!isInteractiveElement && e.deltaY !== 0 && this.boardContainerRef.current) {
			e.preventDefault();
			const container = this.boardContainerRef.current;
			container.scrollLeft += e.deltaY * 0.5;
		}
	};

	private handleColumnsMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
		if (this.state.viewMode !== ViewModeEnum.Columns) return;

		if (this.dndSystem.isDragActive()) return;

		const target = e.target as HTMLElement;
		const isInteractiveElement = target.closest('.task-card') || target.closest('button');

		if (e.button === 0 && !isInteractiveElement && this.boardContainerRef.current) {
			this.setState({
				isGrabbing: true,
				dragStartX: e.clientX,
				dragStartScrollLeft: this.boardContainerRef.current.scrollLeft,
			});
			this.boardContainerRef.current.style.cursor = 'grabbing';
		}
	};

	private handleColumnsMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
		if (this.state.viewMode !== ViewModeEnum.Columns || !this.state.isGrabbing || !this.boardContainerRef.current) {
			return;
		}

		if (this.dndSystem.isDragActive()) return;

		const deltaX = e.clientX - this.state.dragStartX;
		this.boardContainerRef.current.scrollLeft = this.state.dragStartScrollLeft - deltaX;
	};

	private handleColumnsMouseUp = (): void => {
		if (this.state.viewMode !== ViewModeEnum.Columns) return;
		if  (!this.state.isGrabbing) return;

		this.setState({ isGrabbing: false });
		if (this.boardContainerRef.current) {
			this.boardContainerRef.current.style.cursor = '';
		}
	};

	private handleColumnsMouseLeave = (): void => {
		this.handleColumnsMouseUp();
	};

	private handleTouchStart = (e: React.TouchEvent): void => {
		//if (this.state.viewMode !== ViewModeEnum.Columns) return;

		if (this.dndSystem.isDragActive()) return;

		if (e.touches.length !== 1) {
			return;
		}

		const touch = e.touches[0];
		this.setState({
			isSwiping: true,
			swipeStartX: touch.clientX,
			touchStartTime: Date.now(),
			swipeVelocity: 0,
			touchDeltaX: 0, // Инициализируем общее смещение
		});
	};

	private handleTouchMove = (e: React.TouchEvent): void => {
		if (!this.state.isSwiping || this.dndSystem.isDragActive() || e.touches.length !== 1 || !this.boardContainerRef.current) {
			return;
		}

		const touch = e.touches[0];
		const deltaX = this.state.swipeStartX - touch.clientX;
		const currentTime = Date.now();
		const timeDelta = currentTime - this.state.touchStartTime;
		
		// Вычисляем скорость (px/ms)
		const velocity = timeDelta > 0 ? Math.abs(deltaX) / timeDelta : 0;

		if (Math.abs(deltaX) > 5) {
			e.preventDefault();

			this.boardContainerRef.current.scrollLeft += deltaX;
			this.setState({ 
				swipeStartX: touch.clientX,
				swipeVelocity: velocity,
				// Сохраняем общее смещение для определения направления свайпа
				touchDeltaX: (this.state.touchDeltaX || 0) + deltaX
			});
		}
	};

	private handleTouchEnd = (): void => {
		const wasSwiping = this.state.isSwiping;
		this.setState({ isSwiping: false });

		if (!this.boardContainerRef.current || !wasSwiping) {
			return;
		}

		const container = this.boardContainerRef.current;
		const columnsCount = this.getSortedColumns().length;

		// Определяем, был ли это быстрый свайп
		const touchEndTime = Date.now();
		const touchDuration = touchEndTime - this.state.touchStartTime;
		
		// Ужесточаем критерии свайпа
		const isFast = touchDuration < 250; // Быстрее 250ms
		const isFastEnough = true || this.state.swipeVelocity > 0.2; // Скорость > 0.5px/ms
		const hasMinimumMovement = Math.abs(this.state.touchDeltaX || 0) > 30; // Минимальное смещение 30px
		
		const isSwipe = isFast && isFastEnough && hasMinimumMovement;
		console.log(touchDuration, this.state.swipeVelocity, isFast, isFastEnough, hasMinimumMovement, isSwipe);

		let targetIndex = this.state.currentColumnIndex;

		if (isSwipe) {
			// Определяем направление свайпа ПО НАПРАВЛЕНИЮ ДВИЖЕНИЯ ПАЛЬЦА
			const deltaX = this.state.touchDeltaX || 0;
			
			if (deltaX > 0) {
				// Палец двигался ВПРАВО -> свайп ВЛЕВО (предыдущая колонка)
				// Палец двигался ВЛЕВО -> свайп ВПРАВО (следующая колонка)
				if (targetIndex < columnsCount - 1) {
					targetIndex = targetIndex + 1;
				}
			} else if (deltaX < 0) {
				if (targetIndex > 0) {
					targetIndex = targetIndex - 1;
				}
			}

			// Для свайпа скроллим к целевой колонке по ЛЕВОЙ ГРАНИЦЕ
			// Учитываем gap между колонками (из CSS: var(--spacing-md))
			// Получаем реальную ширину колонки через первый элемент или вычисляем
			const columnElements = container.querySelectorAll('.column');
			let columnWidth = container.clientWidth * 0.85; // fallback

			if (columnElements.length > 0 && columnElements[0]) {
				const firstColumn = columnElements[0] as HTMLElement;
				// offsetWidth включает padding, border, но не margin
				// gap реализован через margin или gap у flex-контейнера
				columnWidth = firstColumn.offsetWidth;

				// Если есть gap (обычно 8-16px), добавляем его
				// Проверяем computed style контейнера
				const containerStyle = window.getComputedStyle(container);
				const gap = parseFloat(containerStyle.gap) || 0;
				columnWidth += gap;
			}

			const targetScroll = targetIndex * columnWidth;
			container.scrollTo({
				left: targetScroll,
				behavior: 'smooth'
			});
		}
		// Медленный тач-драг - ничего не делаем, остаемся на текущей позиции
		
		this.setState({ 
			currentColumnIndex: targetIndex,
			touchDeltaX: 0 // Сбрасываем смещение
		});
	};
	//</editor-fold>

	//<editor-fold desc="List Mode Handlers">

	private handleAddTask = (): void => {
		if (this.state.viewMode !== ViewModeEnum.List) return;

		const sortedColumns = this.getSortedColumns();
		if (sortedColumns.length === 0) {
			console.warn('No columns available to add task');
			return;
		}

		const firstColumn = sortedColumns[0];

		this.setState({
			isAddingTask: true,
			addingTaskColumnId: firstColumn.id,
		});

		setTimeout(() => {
			AppSignals.activateTaskAdd.emit(firstColumn.id);

			const columnElement = document.getElementById(firstColumn.id);
			if (columnElement) {
				columnElement.scrollIntoView({
					behavior: 'smooth',
					block: 'start'
				});
			}
		}, 100);
	};

	private handleTaskAdded = (): void => {
		this.setState({
			isAddingTask: false,
			addingTaskColumnId: null,
		});
	};

	private handleCancelAddTask = (): void => {
		this.setState({
			isAddingTask: false,
			addingTaskColumnId: null,
		});
	};

	private onDeleteColumn = (columnId: string): void => {
		this.app.columnDelete(columnId).catch(console.error);
	};
	//</editor-fold>

	//<editor-fold desc="Rendering Methods">

	private renderAddTaskFAB(): React.ReactNode {
		if (this.state.viewMode !== ViewModeEnum.List) return null;

		return (
			<button
				className={`board__add-task-fab ${
					this.state.isAddingTask ? 'board__add-task-fab--hidden' : ''
				}`}
				onClick={this.handleAddTask}
				aria-label="Добавить задачу"
			>
				<span className="board__add-task-fab-icon">{Icons.Add}</span>
			</button>
		);
	}

	private renderColumns(): React.ReactNode {
		const { viewMode} = this.state;
		const sortedColumns = this.getSortedColumns();
		const board = this.getBoard();

		return sortedColumns.map((column, index) => {
			const showDropzone = this.getBoard().isEditing;

			return (
				<React.Fragment key={column.id}>
					{showDropzone && <ColumnDropzone
						board={board}
						index={index}
						isFirst={index === 0}
						isLast={index === sortedColumns.length - 1}
						viewMode={viewMode}
					/>}

					<ColumnComponent
						column={column}
						index={index}
						onDelete={this.onDeleteColumn}
						onTaskAdded={this.handleTaskAdded}
						onCancel={this.handleCancelAddTask}
					/>

					{showDropzone && index === sortedColumns.length - 1 && (
						<ColumnDropzone board={board} index={index + 1} isLast={true} />
					)}
				</React.Fragment>
			);
		});
	}

	private getBoard() {
		return this.props.board;
	}

	private renderScrollIndicators(): React.ReactNode {
		const { viewMode, scrollIndicators } = this.state;

		if (viewMode !== ViewModeEnum.Columns) return null;

		return (
			<>
				{scrollIndicators.left && (
					<div className="board__scroll-indicator board__scroll-indicator--left">
						<div className="board__scroll-indicator-arrow">‹</div>
					</div>
				)}
				{scrollIndicators.right && (
					<div className="board__scroll-indicator board__scroll-indicator--right">
						<div className="board__scroll-indicator-arrow">›</div>
					</div>
				)}
			</>
		);
	}
	//</editor-fold>
	//<editor-fold desc="Main Render">

	render(): React.ReactNode {
		const { viewMode, showFilters, isGrabbing } = this.state;
		const board = this.getBoard();

		const boardClass = `board board--${viewMode} ${
			viewMode === ViewModeEnum.Columns && isGrabbing ? 'board--grabbing' : ''
		} ${board.isEditing ? 'is-editing' : ''}`;

		// События для режима колонок
		const boardEvents = viewMode === ViewModeEnum.Columns ? {
			onWheel: this.handleColumnsWheel,
			onMouseDown: this.handleColumnsMouseDown,
			onMouseMove: this.handleColumnsMouseMove,
			onMouseUp: this.handleColumnsMouseUp,
			onMouseLeave: this.handleColumnsMouseLeave,
			onTouchStart: this.handleTouchStart,
			onTouchMove: this.handleTouchMove,
			onTouchEnd: this.handleTouchEnd,
			onScroll: this.handleColumnsScroll, // ← ВАЖНО: onScroll должен быть здесь!
		} : {};

		return (
			<>
				{/* Фильтры (показываются если включены) */}
				{showFilters && <BoardFilters />}

				{/* Навигация по колонкам */}
				{!board.isEditing && <BoardNav board={this.getBoard()}/>}

				{/* ВАЖНО: ref и события должны быть на этом контейнере! */}
				<div
					className={boardClass}
					ref={this.boardContainerRef}
					{...boardEvents}
				>
					{/* Колонки рендерятся прямо здесь, как в оригинале! */}
					{this.renderColumns()}

					{/* Кнопка добавления колонки в режиме редактирования */}
					{board.isEditing && viewMode === ViewModeEnum.Columns && (
						<div className="board__add-column-container">
							<ColumnAdd />
						</div>
					)}

					{/* Индикаторы скролла */}
					{this.renderScrollIndicators()}

					{/* Кнопка добавления задачи (FAB для режима списка) */}
					{this.renderAddTaskFAB()}
				</div>
			</>
		);
	}
	//</editor-fold>
}

export default BoardComponent;